/**
 * Authentication API service.
 *
 * All authentication flows go through the backend.
 * The frontend never talks directly to Supabase.
 */

import axios from 'axios'

const API_URL = import.meta.env.VITE_API_URL || '/api'

// Create a separate axios instance for auth (no interceptors that depend on auth)
const authApi = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
})

export interface AuthUser {
  id: string
  email: string
  full_name?: string | null
  onboarding_completed?: boolean
  active_organization_id?: string | null
  created_at?: string | null
}

export interface AuthTokens {
  access_token: string
  refresh_token: string
  expires_at: number
}

export interface AuthResponse extends AuthTokens {
  user: AuthUser
}

export interface SignInRequest {
  email: string
  password: string
}

export interface SignUpRequest {
  email: string
  password: string
  full_name?: string
}

/**
 * Sign in with email and password.
 */
export async function signIn(request: SignInRequest): Promise<AuthResponse> {
  const response = await authApi.post<AuthResponse>('/v1/auth/signin', request)
  return response.data
}

/**
 * Sign up with email and password.
 */
export async function signUp(request: SignUpRequest): Promise<AuthResponse> {
  const response = await authApi.post<AuthResponse>('/v1/auth/signup', request)
  return response.data
}

/**
 * Refresh the access token.
 */
export async function refreshToken(refreshToken: string): Promise<AuthResponse> {
  const response = await authApi.post<AuthResponse>('/v1/auth/refresh', {
    refresh_token: refreshToken
  })
  return response.data
}

/**
 * Sign out (just for API consistency).
 */
export async function signOut(): Promise<void> {
  await authApi.post('/v1/auth/signout')
}

/**
 * Request password reset email.
 */
export async function forgotPassword(email: string): Promise<{ message: string }> {
  const response = await authApi.post<{ message: string }>('/v1/auth/forgot-password', { email })
  return response.data
}

/**
 * Reset password with access token from recovery flow.
 */
export async function resetPassword(
  accessToken: string,
  refreshToken: string,
  newPassword: string
): Promise<{ message: string }> {
  const response = await authApi.post<{ message: string }>('/v1/auth/reset-password', {
    access_token: accessToken,
    refresh_token: refreshToken,
    new_password: newPassword
  })
  return response.data
}
